import { Plus } from "lucide-react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useSelectionStore } from "@/store/selection";
import { useSessionStore } from "@/store/session";
import { useOrderStore } from "@/store/order";
import { useTotalPrice } from "@/models/price";
import { useSunProtectionPageContent } from "@/models/sun-protection-settings";
import { PriceSummary } from "./confirm/PriceSummary";
import { SelectedWindowsList } from "./confirm/SelectedWindowsList";
import { useConfirmRows } from "./confirm/useConfirmRows";

export function ConfirmStep() {
    const { t } = useTranslation();
    const [, setSearchParams] = useSearchParams();
    const removeProductFromCombo = useSelectionStore(
        (s) => s.removeProductFromCombo,
    );
    const discountCode = useSessionStore((s) => s.discountCode);
    const error = useOrderStore((s) => s.error);
    const signatureUrl = useOrderStore((s) => s.signatureUrl);
    const { totals } = useTotalPrice();
    const { data: pageContent } = useSunProtectionPageContent();
    const { groupedEntries, totalProductCount } = useConfirmRows();

    const canAddProduct = !signatureUrl;

    function handleAddProduct() {
        setSearchParams(
            (prev) => {
                prev.set("step", "0");
                return prev;
            },
            { replace: false },
        );
    }

    return (
        <div className="flex flex-col gap-8">
            <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 leading-tight">
                    {t("confirmStep.title")}
                </h1>
                <p className="mt-3 text-base text-gray-500 max-w-lg mx-auto">
                    {t("confirmStep.subtitle")}
                </p>
            </div>

            <SelectedWindowsList
                groupedEntries={groupedEntries}
                onRemoveChip={(entry, chip) =>
                    removeProductFromCombo(
                        chip.category,
                        chip.typeId,
                        entry.windowId,
                    )
                }
            />

            {canAddProduct && (
                <button
                    type="button"
                    onClick={handleAddProduct}
                    data-testid="add-another-product"
                    className="self-center inline-flex items-center gap-1.5 rounded-full border border-primary px-6 py-3 text-sm font-semibold text-primary cursor-pointer hover:bg-primary/5"
                >
                    <Plus className="size-4" aria-hidden="true" />
                    {t("confirmStep.addAnotherProduct")}
                </button>
            )}

            <PriceSummary
                totals={totals}
                discountCode={discountCode}
                totalProductCount={totalProductCount}
                pricesIncludeVat={pageContent?.pricesIncludeVat}
            />

            {error && (
                <p data-testid="submit-error" className="text-sm text-red-500">
                    {t(error)}
                </p>
            )}
        </div>
    );
}
